/**
 * useSubscriptionGuard
 *
 * Action-level paywall hook. Wraps any paid action with a check: if isPaid,
 * runs the action; if free, surfaces the upgrade modal with a contextual
 * action label.
 *
 *   const { isPaid, requirePaid } = useSubscriptionGuard();
 *   <button onClick={() => requirePaid('Email Coach Smith at Michigan', sendEmail)}>
 *     Email Coach
 *   </button>
 *
 * The modal itself is mounted exactly once at App.tsx root inside
 * <SubscriptionProvider> (see <UpgradeModal />). The hook reads modalAction /
 * dismissModal off the same context so any component can trigger the single
 * shared modal instance — no per-page modal mounting.
 *
 * Spec: /web/ONBOARDING_FREEMIUM_REDESIGN_PLAN.md §8 Phase 1 B2.
 */

import { useSubscription } from '../contexts/SubscriptionContext';

export interface SubscriptionGuard {
  /** True iff the requesting user's family currently has paid access. */
  isPaid: boolean;
  /**
   * Run `fn` if paid; otherwise surface the upgrade modal with `actionLabel`
   * shown in the header. `fn` may be sync or async — returned promise (if any)
   * is preserved so callers can `await requirePaid(...)`.
   */
  requirePaid: (actionLabel: string, fn: () => void | Promise<void>) => void;
  /** Currently-surfaced action label (null when no modal showing). */
  modalAction: string | null;
  /** Close the modal without running anything. */
  dismissModal: () => void;
}

export function useSubscriptionGuard(): SubscriptionGuard {
  const { isPaid, modalAction, setModalAction, dismissModal } = useSubscription();

  const requirePaid: SubscriptionGuard['requirePaid'] = (actionLabel, fn) => {
    if (isPaid) {
      fn();
    } else {
      setModalAction(actionLabel);
    }
  };

  return { isPaid, requirePaid, modalAction, dismissModal };
}
